import React, { useState } from "react";

import {
  filter,
  flatten,
  isEmpty,
  isEqual,
  map,
  reject,
  uniq,
} from "lodash/fp";

import FormControlLabel from "@mui/material/FormControlLabel";

import useTopicReport from "@app/analytics/TopicReport/hooks/useTopicReport";
import useTopicReportEntities from "@app/analytics/TopicReport/hooks/useTopicReportEntities";
import type { TopicReportDistributionChannel } from "@app/analytics/TopicReport/types";
import type { TopicEntity } from "@app/analytics/types";
import {
  Dialog,
  CloseButton,
  Container,
  ContentContainer,
  Header,
} from "@app/common/access/ShareReport/styles";
import { MESSAGING_BOT_PROVIDERS_BY_ID } from "@app/common/constants/providers";

import Accordion from "@rui/Accordion";
import Alert from "@rui/Alert";
import Checkbox from "@rui/Checkbox";
import ModalTitle from "@rui/Modal/ModalTitle";
import { Column, Row } from "@rui/Stack";
import { Span } from "@rui/styles";

import SendToSlackContent from "./SendToSlackContent";
import SendToTeamsContent from "./SendToTeamsContent";
import SendViaEmailContent from "./SendViaEmailContent";
import { CustomizeAccordionDetails, CustomizeAccordionSummary } from "./styles";
import type { DialogContentProps } from "./types";

const Disclaimer = () => (
  <Alert embedded alignItems="flex-start" severity="info">
    <Span color="shades.neutral.600">
      Sending this report will not post a link to the report, share sources, or
      include citations.
    </Span>
  </Alert>
);

const CONTENT_FOR_DISTRIBUTION_CHANNEL: {
  [key in TopicReportDistributionChannel]: React.FunctionComponent<DialogContentProps>;
} = {
  microsoft_graph_teams_push: SendToTeamsContent,
  slack_bot: SendToSlackContent,
  email: SendViaEmailContent,
};

interface EntityCheckboxConfig {
  kind: TopicEntity["kind"];
  label: string;
  disabled: boolean;
  show: boolean;
}

const getCheckboxConfig = (
  label: string,
  kind: TopicEntity["kind"],
  entityOrEntities?: string | TopicEntity | TopicEntity[]
): EntityCheckboxConfig => {
  if (Array.isArray(entityOrEntities)) {
    const hasEntities = !isEmpty(entityOrEntities);
    return {
      kind,
      label: `${label}${hasEntities ? ` (${entityOrEntities.length})` : ""}`,
      show: true,
      disabled: !hasEntities,
    };
  }

  return {
    kind,
    label,
    show: true,
    disabled: !entityOrEntities,
  };
};

const SendViaDialog = ({
  channel,
  onClose,
}: {
  channel: TopicReportDistributionChannel;
  onClose: () => void;
}) => {
  const { topic } = useTopicReport();
  const { actionItems, dailyUpdates, keyQuestions } = useTopicReportEntities();

  const entityCheckboxes = [
    [
      getCheckboxConfig("Summary", "summary", topic?.summary ?? ""),
      getCheckboxConfig("Action Items", "action_item", actionItems),
    ],
    [
      getCheckboxConfig("Daily Updates", "daily_summary", dailyUpdates),
      getCheckboxConfig("Key Questions", "key_question", keyQuestions),
    ],
  ];

  const defaultSelectedEntities = map(
    "kind",
    filter({ disabled: false, show: true }, flatten(entityCheckboxes))
  );

  const [selectedEntityKinds, setSelectedEntityKinds] = useState<
    TopicEntity["kind"][]
  >(defaultSelectedEntities);

  // to appease the types
  if (!topic) return null;

  const isEmailChannel = channel === "email";

  const providerConfig = isEmailChannel
    ? undefined
    : MESSAGING_BOT_PROVIDERS_BY_ID[channel];
  const channelDisplayName = providerConfig?.displayNameShort ?? "email";

  const Content = CONTENT_FOR_DISTRIBUTION_CHANNEL[channel];

  const handleToggleEntityKind = (
    kind: TopicEntity["kind"],
    checked: boolean
  ) => {
    if (checked) {
      setSelectedEntityKinds(uniq([...selectedEntityKinds, kind]));
    } else {
      setSelectedEntityKinds(reject(isEqual(kind), selectedEntityKinds));
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <Container>
        <Header variant="apart">
          <ModalTitle>
            Send {isEmailChannel ? "via" : "to"} {channelDisplayName}
          </ModalTitle>
          <CloseButton onClick={onClose} />
        </Header>
        <ContentContainer marginTop={2} spacing={2}>
          <Disclaimer />
          <Accordion defaultExpanded>
            <CustomizeAccordionSummary>Customize</CustomizeAccordionSummary>
            <CustomizeAccordionDetails>
              <Row paddingTop={0.5}>
                {entityCheckboxes.map((column, i) => (
                  <Column key={i} flexBasis="50%">
                    {column.map(({ kind, label, disabled, show }) => {
                      if (!show) return null;
                      return (
                        <FormControlLabel
                          key={kind}
                          control={<Checkbox variant="filled" />}
                          label={label}
                          disabled={disabled}
                          checked={
                            !disabled && selectedEntityKinds.includes(kind)
                          }
                          onChange={(e, checked) =>
                            handleToggleEntityKind(kind, checked)
                          }
                        />
                      );
                    })}
                  </Column>
                ))}
              </Row>
            </CustomizeAccordionDetails>
          </Accordion>
        </ContentContainer>
        <Content
          contributors={topic.contributors}
          onClose={onClose}
          selectedEntityKinds={selectedEntityKinds}
          topicId={topic.id}
        />
      </Container>
    </Dialog>
  );
};

export default SendViaDialog;
